import { View } from "../view";
import Prism from "../prism";
import "./snippets.css";
import codeSnippetHtml from "./code-snippet.html";
import codeFileHtml from "./code-file.html";
import { interpolateHtml } from "../html";
import TreeView from 'js-treeview';
import '../treeview.css';
import { Filter } from "./filter";
export class Snippets {
    static async getRootNode() {
        return Snippets.ROOT_NODE = await View.getRootNode(Snippets.ROOT_NODE, Snippets.ROOT_NODE_CLASS_NAME);
    }
    static generateCodeBody(startLine, endLine, linesOfCode, title = "") {
        return interpolateHtml(codeSnippetHtml, new Map([
            [`//div[@class="code-body"]/@data-start-line`, startLine],
            [`//div[@class="code-body"]/@data-end-line`, endLine],
            [`//div[@class="code-metadata"]/div[@class="code-title"]/text()`, title],
            [`//div[@class="code-snippet"]/pre/@data-start`, startLine],
            [`//div[@class="code-snippet"]/pre/@data-line-offset`, startLine],
            [`//div[@class="code-snippet"]/pre/code/text()`, linesOfCode.join('')]
        ]));
    }
    static generateCodeFile(filePath) {
        return interpolateHtml(codeFileHtml, new Map([
            [`//div[@class="code-file"]/@data-file-path`, filePath],
            [`//div[@class="code-file-path"]/text()`, filePath]
        ]));
    }
    constructor(reachability) {
        this.reachability = reachability;
        this.filter = new Filter(this.reachability);
    }
    displayLoading(message) {
        console.log("Loading", message, this.reachability);
    }
    createSyscallFilters(syscalls) {
        let style = document.createElement('style');
        document.documentElement.appendChild(style);
        syscalls.forEach(s => {
            style.sheet?.insertRule(`.filter-syscall[data-filter-syscall~=${JSON.stringify(s)}] .filtered-syscall[data-filtered-syscall~=${JSON.stringify(s)}] {
                    --filtered-syscall-highlight: var(--filtered-syscall-highlight-hit);
                    --filtered-syscall-color: var(--filtered-syscall-color-hit);
                    --filtered-syscall-outline: var(--filtered-syscall-outline-hit);
                    --filtered-syscall-opacity: var(--filtered-syscall-opacity-hit);
                    --filtered-syscall-appearance: var(--filtered-syscall-appearance-hit);
                    --filtered-syscall-display: var(--filtered-syscall-display-hit);
                }`);
        });
    }
    async displaySnippet(parentNode, filePath, linesOfCode, startLine = "", title = "", childEdgesPromise = null, parentEdgesPromise = null, coveragePromise = null, syscallsPromise = null, syzkallsPromise = null, conditionsPromise = null) {
        let fileNode = parentNode.querySelector(`& > .code-file[data-file-path="${filePath}"]`);
        let endLine = String(Number(startLine) + linesOfCode.length - 1);
        if (!fileNode) {
            fileNode = this.createNewFileNode(filePath, parentNode);
        }
        let fileCodeSnippets = fileNode.querySelector('& > .code-snippets');
        let surroundings = this.getSurroundingSnippets(startLine, [...fileCodeSnippets.querySelectorAll('& > .code-body')]);
        if (surroundings.existingElement) {
            return this.selectExistingElement(surroundings.existingElement);
        }
        let codyBodyFragment = Snippets.generateCodeBody(startLine, endLine, linesOfCode, title);
        let parentFiles = codyBodyFragment.querySelector('.parent-files');
        let parentBrowser = codyBodyFragment.querySelector('.parent-browser');
        let codeSnippet = codyBodyFragment.querySelector('.code-snippet pre');
        let codeMetadata = codyBodyFragment.querySelector('.code-metadata');
        let codeFilter = codyBodyFragment.querySelector('.code-filter');
        if (coveragePromise) {
            let coverage = await coveragePromise;
            codeSnippet.dataset.line = [...new Set(coverage.map(c => c.code_line_no))].join(',');
        }
        fileCodeSnippets.insertBefore(codyBodyFragment, surroundings.nextInSequence);
        Prism.highlightElement(codeSnippet.querySelector('code'));
        this.focusElement(codeSnippet);
        if (childEdgesPromise) {
            this.displayChildEdges(childEdgesPromise, fileNode, codeSnippet, filePath, startLine);
        }
        if (parentEdgesPromise) {
            this.displayParentEdges(parentEdgesPromise, parentFiles, parentBrowser);
        }
        if (syscallsPromise && syzkallsPromise && conditionsPromise) {
            this.displaySyscalls(syscallsPromise, syzkallsPromise, conditionsPromise, codeMetadata, codeFilter);
        }
        return codeSnippet;
    }
    addLabel(container, className, value) {
        let labelNode = container.appendChild(document.createElement('div'));
        labelNode.className = 'label ' + className;
        labelNode.appendChild(document.createTextNode(value));
        return labelNode;
    }
    createNewFileNode(filePath, parentNode) {
        let newFileFragment = Snippets.generateCodeFile(filePath);
        let fileNode = newFileFragment.querySelector('.code-file');
        fileNode.addEventListener('change', (e) => {
            this.focusElement(e.target);
            this.reachability.updateViews();
        });
        parentNode.appendChild(newFileFragment);
        return fileNode;
    }
    selectExistingElement(existingElement) {
        const snippetCssPath = '> .code-metadata :checked';
        const fileCssPath = '> .code-snippets > .code-file-path-container :checked';
        let collapsedSnippet, collapsedFile;
        do {
            collapsedSnippet = existingElement.closest(`.code-body:has(${snippetCssPath})`);
            collapsedFile = existingElement.closest(`.code-file:has(${fileCssPath})`);
            console.log(collapsedSnippet, collapsedFile);
            if (collapsedSnippet) {
                collapsedSnippet.querySelector(`& ${snippetCssPath}`).checked = false;
            }
            if (collapsedFile) {
                collapsedFile.querySelector(`& ${fileCssPath}`).checked = false;
            }
        } while (collapsedSnippet || collapsedFile);
        let codeSnippet = existingElement.querySelector('& > .code-snippet > pre');
        this.focusElement(codeSnippet);
        return codeSnippet;
    }
    async displayChildEdges(childEdgesPromise, fileNode, codeSnippet, filePath, startLine) {
        let childEdges = await childEdgesPromise;
        this.decorateChildEdges(fileNode.querySelector('& > .child-files'), codeSnippet, childEdges, filePath, startLine);
        this.reachability.updateViews();
    }
    async displayParentEdges(parentEdgesPromise, parentFiles, parentBrowser) {
        let parentEdges = await parentEdgesPromise;
        this.showParentEdges(parentFiles, parentBrowser, parentEdges);
        parentBrowser.dataset.loadingFinished = "true";
        this.reachability.updateViews();
    }
    async displaySyscalls(syscallsPromise, syskallsPromise, conditionsPromise, codeMetadata, codeFilter) {
        let syscalls = await syscallsPromise;
        let syscallsLabel = this.addLabel(codeMetadata, syscalls.length ? 'label-syscalls' : '', `syscalls:${syscalls.length}`);
        if (syscalls.length) {
            syscallsLabel.addEventListener('click', async () => {
                await this.filter.displaySyscalls(syscalls, syskallsPromise, conditionsPromise, codeFilter);
                this.reachability.updateViews();
            });
        }
    }
    focusElement(codeSnippet) {
        codeSnippet.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest"
        });
    }
    getSurroundingSnippets(targetLine, codeSnippets) {
        let nextInSequence = null;
        let lastInSequence = null;
        let existingElement = null;
        for (let elem of codeSnippets) {
            let htmlNode = elem;
            if (htmlNode.dataset.startLine && Number(htmlNode.dataset.startLine) <= Number(targetLine) &&
                htmlNode.dataset.endLine && Number(htmlNode.dataset.endLine) >= Number(targetLine)) {
                existingElement = htmlNode;
                break;
            }
            if (htmlNode.dataset.startLine && Number(htmlNode.dataset.startLine) >= Number(targetLine)) {
                nextInSequence = htmlNode;
                break;
            }
            lastInSequence = htmlNode;
        }
        return { existingElement, lastInSequence, nextInSequence };
    }
    decorateChildEdges(childFiles, snippet, edges, filePath, startLine) {
        [...snippet.querySelectorAll('.token.function')].forEach(node => {
            let functionName = node.textContent;
            let matchingEdges = edges.filter(edge => edge.source_message == "call to " + functionName);
            if (matchingEdges.length == 1) {
                const edge = matchingEdges[0];
                node.classList.add('navigation-edge');
                node.addEventListener('click', async () => {
                    this.reachability.showFileLine(childFiles, edge.target_uri, Number(edge.target_startLine), Number(edge.target_function_end_line), functionName, node);
                });
            }
            else {
                node.classList.add('navigation-missing-edge');
                node.addEventListener('click', async () => {
                    const range = document.createRange();
                    range.setStartBefore(snippet);
                    range.setEndBefore(node);
                    const lineOffset = range.cloneContents().textContent.split(/\n/).length - 1;
                    const lineNumber = Number(startLine) + lineOffset;
                    this.reachability.displayEdgeSelection(functionName, filePath, lineNumber, node, childFiles);
                });
            }
        });
        [...snippet.querySelectorAll('.token.class-name')].forEach(node => {
            let structName = node.textContent;
            node.classList.add('navigation-struct');
            node.addEventListener('click', async () => {
                location.hash = `#!heap/*/${structName}`;
            });
        });
    }
    showParentEdges(parentFiles, parentBrowser, edges) {
        let root = { name: '', children: [] };
        let expanded = edges.length < 30;
        edges.forEach(edge => {
            if (!edge.source_function_names)
                return;
            let pathParts = [...edge.source_uri.split('/'), edge.source_function_names];
            let leaf = root;
            pathParts.forEach(part => {
                let nextLeaf = leaf.children.find(e => e.name == part);
                if (!nextLeaf) {
                    nextLeaf = {
                        expanded: expanded,
                        name: part.replace(/,/g, '\n'),
                        children: [],
                        data: { syscalls: [] }
                    };
                    leaf.children.push(nextLeaf);
                }
                leaf = nextLeaf;
                leaf.data = { syscalls: [...new Set(leaf.data?.syscalls), ...edge.syscalls.split(' ')] };
            });
            leaf.data = { edge, syscalls: [...edge.syscalls.split(' ')] };
        });
        const treeview = new TreeView(root.children, parentBrowser);
        treeview.on('expand', () => this.reachability.updateViews());
        treeview.on('collapse', () => this.reachability.updateViews());
        treeview.on('select', event => {
            const edge = event.data.data.edge;
            if (!edge)
                return;
            this.reachability.showFileLine(parentFiles, edge.source_uri, Number(edge.source_function_start_line), Number(edge.source_function_end_line), event.data.name, null);
        });
        parentBrowser.querySelectorAll('.tree-leaf-content').forEach(node => {
            let nodeDiv = node;
            let nodeJson = JSON.parse(nodeDiv.dataset.item || '{}');
            nodeDiv.dataset.filteredSyscall = nodeJson.data.syscalls.join(' ');
            nodeDiv.classList.add('filtered-syscall');
        });
    }
}
Snippets.ROOT_NODE = null;
Snippets.ROOT_NODE_CLASS_NAME = 'snippets-root-node';
